import { useCallback, useEffect, useState } from 'react'
import {
  Button,
  Drawer,
  Input,
  Modal,
  Pagination,
  Progress,
  Segmented,
  Space,
  Switch,
  Table,
  Tag,
  Typography,
  message,
} from 'antd'
import { ExperimentOutlined, PlayCircleOutlined, StarOutlined } from '@ant-design/icons'
import { adminApi, formatApiError, PAGE_SIZE, type PageResult } from '../../api'
import { GoldDocEditor, type GoldDoc } from '../../components/GoldDocEditor'
import { KNOWLEDGE_SCOPE_OPTIONS_SHORT } from '../../constants/knowledgeScope'
import { formatDate, listPagination, tablePagination, usePagedQuery, useServerPagination } from '../../components/ui'

const { Text, Paragraph } = Typography

type CaseFilter = 'all' | 'good' | 'bad' | 'unset'
type GoldFilter = 'all' | 'ready' | 'missing'

type RetrievalCase = {
  id: number
  question: string
  scope: string
  case_label: string
  is_benchmark: boolean
  eval_gold_docs: GoldDoc[]
  eval_top_k: number
  gold_ready: boolean
  created_at: string
}

type RetrievalSummary = {
  total_cases?: number
  win?: number
  tie?: number
  lose?: number
  retrieval_win_rate?: number
  recall_rate?: number
  avg_primary_gold_rank?: number
  avg_ndcg_at_k?: number
  good?: {
    avg_primary_gold_rank_baseline?: number
    avg_primary_gold_rank_candidate?: number
    avg_ndcg_baseline?: number
    avg_ndcg_candidate?: number
  }
  bad?: {
    recall_rate_baseline?: number
    recall_rate_candidate?: number
    new_recall_count?: number
  }
}

type BatchListItem = {
  batch_id: string
  experiment_name: string
  baseline_batch_id: string
  is_baseline: boolean
  status: string
  progress: number
  status_message: string
  total_cases: number
  finished_cases: number
  summary?: RetrievalSummary | null
  error_message?: string | null
  created_at: string
}

type BatchDetailItem = {
  run_id: number
  record_id: number
  question: string
  case_label: string
  gold_item_ids: number[]
  metrics: Record<string, unknown>
  baseline_metrics: Record<string, unknown>
  baseline_documents?: { item_id: number; title: string; rank: number; score: number; is_gold?: boolean }[]
  documents: { item_id: number; title: string; rank: number; score: number }[]
  verdict: string
  compare?: { reasons?: string[]; ndcg_delta?: number; rank_delta?: number } | null
  error_message?: string | null
}

type BatchDetail = {
  batch_id: string
  experiment_name: string
  baseline_batch_id: string
  is_baseline: boolean
  summary?: RetrievalSummary | null
  status: string
  progress: number
  status_message: string
  total_cases: number
  finished_cases: number
  error_message?: string | null
  items: BatchDetailItem[]
  total: number
  page: number
  page_size: number
  created_at: string
}

type BaselineInfo = {
  batch_id: string
  experiment_name: string
  created_at: string
  summary?: RetrievalSummary | null
}

const empty = (p: number): PageResult<RetrievalCase> => ({
  items: [],
  total: 0,
  page: p,
  page_size: PAGE_SIZE,
})

function caseTag(label: string) {
  if (label === 'good') return <Tag color="success">Good</Tag>
  if (label === 'bad') return <Tag color="error">Bad</Tag>
  return <Tag>未分类</Tag>
}

function verdictTag(v: string) {
  if (v === 'win') return <Tag color="success">提升</Tag>
  if (v === 'lose') return <Tag color="error">回退</Tag>
  if (v === 'tie') return <Tag>持平</Tag>
  return null
}

function batchStatusTag(status: string) {
  if (status === 'running') return <Tag color="processing">运行中</Tag>
  if (status === 'pending') return <Tag>排队中</Tag>
  if (status === 'error') return <Tag color="error">失败</Tag>
  return <Tag color="success">已完成</Tag>
}

function metricCell(m: Record<string, unknown>, key: string) {
  const v = m[key]
  if (v === null || v === undefined) return '—'
  if (typeof v === 'boolean') return v ? '是' : '否'
  return String(v)
}

export default function RetrievalEvalPanel() {
  const [benchmarkOnly, setBenchmarkOnly] = useState(true)
  const [caseFilter, setCaseFilter] = useState<CaseFilter>('all')
  const [goldFilter, setGoldFilter] = useState<GoldFilter>('all')
  const [keyword, setKeyword] = useState('')
  const [selectedIds, setSelectedIds] = useState<number[]>([])
  const [running, setRunning] = useState(false)
  const [detail, setDetail] = useState<RetrievalCase | null>(null)
  const [previewOpen, setPreviewOpen] = useState(false)
  const [previewLoading, setPreviewLoading] = useState(false)
  const [previewResult, setPreviewResult] = useState<{
    metrics: Record<string, unknown>
    documents: BatchDetailItem['documents']
  } | null>(null)

  const [baseline, setBaseline] = useState<BaselineInfo | null>(null)
  const [runOpen, setRunOpen] = useState(false)
  const [runName, setRunName] = useState('')
  const [compareBaseline, setCompareBaseline] = useState(true)
  const [runTarget, setRunTarget] = useState<'benchmark' | 'selected' | 'bad'>('benchmark')

  const [batchListOpen, setBatchListOpen] = useState(false)
  const [batchList, setBatchList] = useState<BatchListItem[]>([])
  const batchPag = useServerPagination()
  const batchDetailPag = useServerPagination()
  const [batchTotal, setBatchTotal] = useState(0)
  const [batchLoading, setBatchLoading] = useState(false)
  const [batchDetailOpen, setBatchDetailOpen] = useState(false)
  const [batchDetail, setBatchDetail] = useState<BatchDetail | null>(null)
  const [batchDetailLoading, setBatchDetailLoading] = useState(false)

  const loadPage = useCallback(
    (p: number, ps: number) => {
      const params: Record<string, unknown> = {
        page: p,
        page_size: ps,
        benchmark_only: benchmarkOnly,
      }
      if (keyword.trim()) params.q = keyword.trim()
      if (caseFilter !== 'all') params.case_label = caseFilter
      if (goldFilter !== 'all') params.gold_ready = goldFilter
      return adminApi
        .get<PageResult<RetrievalCase>>('/admin/retrieval-eval/cases', { params })
        .then((r) => r.data)
        .catch(() => empty(p))
    },
    [benchmarkOnly, keyword, caseFilter, goldFilter],
  )

  const {
    page,
    setPage,
    total,
    items,
    loading,
    reload,
    handlePaginationChange,
    paginationOptions,
  } = usePagedQuery(loadPage)

  useEffect(() => {
    setPage(1)
  }, [benchmarkOnly, caseFilter, goldFilter, keyword, setPage])

  const reloadBaseline = useCallback(() => {
    adminApi
      .get<BaselineInfo>('/admin/retrieval-eval/baseline')
      .then((r) => setBaseline(r.data.batch_id ? r.data : null))
      .catch(() => setBaseline(null))
  }, [])

  useEffect(() => {
    reloadBaseline()
  }, [reloadBaseline])

  const reloadBatches = useCallback((p = batchPag.page, ps = batchPag.pageSize) => {
    setBatchLoading(true)
    return adminApi
      .get<PageResult<BatchListItem>>('/admin/retrieval-eval/batches', {
        params: { page: p, page_size: ps },
      })
      .then((r) => {
        setBatchList(r.data.items)
        setBatchTotal(r.data.total)
      })
      .catch(() => {
        setBatchList([])
        setBatchTotal(0)
      })
      .finally(() => setBatchLoading(false))
  }, [batchPag.page, batchPag.pageSize])

  useEffect(() => {
    if (batchListOpen) void reloadBatches()
  }, [batchListOpen, batchPag.page, batchPag.pageSize, reloadBatches])

  const openBatchDetail = useCallback((batchId: string, p = batchDetailPag.page, ps = batchDetailPag.pageSize, silent = false) => {
    setBatchDetailOpen(true)
    if (!silent) setBatchDetailLoading(true)
    adminApi
      .get<BatchDetail>(`/admin/retrieval-eval/batches/${batchId}`, {
        params: { page: p, page_size: ps },
      })
      .then((r) => setBatchDetail(r.data))
      .catch((e) => message.error(formatApiError(e)))
      .finally(() => {
        if (!silent) setBatchDetailLoading(false)
      })
  }, [batchDetailPag.page, batchDetailPag.pageSize])

  useEffect(() => {
    const listRunning =
      batchListOpen && batchList.some((b) => b.status === 'pending' || b.status === 'running')
    const detailRunning =
      batchDetailOpen &&
      batchDetail != null &&
      (batchDetail.status === 'pending' || batchDetail.status === 'running')
    if (!listRunning && !detailRunning) return
    const t = window.setInterval(() => {
      if (batchListOpen) void reloadBatches()
      if (detailRunning && batchDetail) {
        openBatchDetail(batchDetail.batch_id, batchDetailPag.page, batchDetailPag.pageSize, true)
      }
    }, 3000)
    return () => window.clearInterval(t)
  }, [
    batchList,
    batchListOpen,
    batchDetail,
    batchDetailOpen,
    batchDetailPag.page,
    batchDetailPag.pageSize,
    batchPag.page,
    batchPag.pageSize,
    openBatchDetail,
    reloadBatches,
  ])

  const startRun = () => {
    const body: Record<string, unknown> = {
      experiment_name: runName.trim(),
      compare_to_active_baseline: compareBaseline && !!baseline?.batch_id,
    }
    if (runTarget === 'selected') {
      if (!selectedIds.length) {
        message.warning('请先勾选用例')
        return
      }
      body.record_ids = selectedIds
    } else if (runTarget === 'bad') {
      body.case_label = 'bad'
      body.benchmark_only = benchmarkOnly
    } else {
      body.benchmark_only = true
    }
    setRunning(true)
    adminApi
      .post<{ batch_id: string; message: string }>('/admin/retrieval-eval/run', body)
      .then((r) => {
        message.success(r.data.message || '已启动检索评测')
        setRunOpen(false)
        setBatchListOpen(true)
        void reloadBatches(1)
        openBatchDetail(r.data.batch_id)
      })
      .catch((e) => message.error(formatApiError(e)))
      .finally(() => setRunning(false))
  }

  const previewOne = (row: RetrievalCase) => {
    setPreviewLoading(true)
    setPreviewOpen(true)
    setPreviewResult(null)
    adminApi
      .post<{ result: { metrics: Record<string, unknown>; documents: BatchDetailItem['documents'] } }>(
        `/admin/retrieval-eval/cases/${row.id}/preview`,
      )
      .then((r) => setPreviewResult(r.data.result))
      .catch((e) => {
        message.error(formatApiError(e))
        setPreviewOpen(false)
      })
      .finally(() => setPreviewLoading(false))
  }

  const markBaseline = (batchId: string) => {
    adminApi
      .post(`/admin/retrieval-eval/batches/${batchId}/mark-baseline`)
      .then(() => {
        message.success('已设为检索基线')
        reloadBaseline()
        void reloadBatches()
        if (batchDetail?.batch_id === batchId) {
          openBatchDetail(batchId, batchDetailPag.page, batchDetailPag.pageSize, true)
        }
      })
      .catch((e) => message.error(formatApiError(e)))
  }

  return (
    <div>
      <Space wrap style={{ marginBottom: 16 }}>
        <Input.Search
          allowClear
          placeholder="搜索问题"
          style={{ width: 240 }}
          value={keyword}
          onChange={(e) => setKeyword(e.target.value)}
          onSearch={(v) => {
            setKeyword(v)
            setPage(1)
          }}
        />
        <Segmented
          value={caseFilter}
          onChange={(v) => setCaseFilter(v as CaseFilter)}
          options={[
            { label: '全部分类', value: 'all' },
            { label: 'Good', value: 'good' },
            { label: 'Bad', value: 'bad' },
            { label: '未分类', value: 'unset' },
          ]}
        />
        <Segmented
          value={goldFilter}
          onChange={(v) => setGoldFilter(v as GoldFilter)}
          options={[
            { label: '全部 Gold', value: 'all' },
            { label: '已标注', value: 'ready' },
            { label: '未标注', value: 'missing' },
          ]}
        />
        <span>
          只展示星标 <Switch checked={benchmarkOnly} onChange={setBenchmarkOnly} />
        </span>
        {baseline?.batch_id ? (
          <Tag icon={<StarOutlined />} color="gold">
            基线：{baseline.experiment_name || baseline.batch_id}
          </Tag>
        ) : (
          <Tag>尚无检索基线</Tag>
        )}
        <Button icon={<ExperimentOutlined />} onClick={() => {
          setBatchListOpen(true)
          void reloadBatches(1)
        }}>
          评测批次
        </Button>
        <Button
          type="primary"
          icon={<PlayCircleOutlined />}
          loading={running}
          onClick={() => setRunOpen(true)}
        >
          运行检索评测
        </Button>
      </Space>

      <Table
        rowKey="id"
        loading={loading}
        dataSource={items}
        rowSelection={{
          selectedRowKeys: selectedIds,
          onChange: (keys) => setSelectedIds(keys.map(Number)),
        }}
        pagination={tablePagination(page, total, handlePaginationChange, paginationOptions)}
        columns={[
          {
            title: '分类',
            width: 88,
            render: (_, r) => caseTag(r.case_label),
          },
          {
            title: 'Gold',
            width: 72,
            render: (_, r) =>
              r.gold_ready ? <Tag color="success">已设</Tag> : <Tag color="warning">未设</Tag>,
          },
          {
            title: '问题',
            render: (_, r) => (
              <div>
                <Text strong>{r.question}</Text>
                {r.eval_gold_docs.length ? (
                  <div>
                    {r.eval_gold_docs.map((d) => (
                      <Tag key={d.item_id} style={{ marginTop: 4 }}>
                        #{d.item_id} {d.title}
                      </Tag>
                    ))}
                  </div>
                ) : null}
              </div>
            ),
          },
          {
            title: '库',
            dataIndex: 'scope',
            width: 72,
            render: (v: string) =>
              KNOWLEDGE_SCOPE_OPTIONS_SHORT.find((o) => o.value === v)?.label || v,
          },
          {
            title: 'TopK',
            dataIndex: 'eval_top_k',
            width: 56,
          },
          {
            title: '时间',
            dataIndex: 'created_at',
            width: 150,
            render: (v: string) => formatDate(v),
          },
          {
            title: '操作',
            width: 160,
            render: (_, r) => (
              <Space wrap size={[4, 4]}>
                <Button size="small" onClick={() => setDetail(r)}>
                  标注 Gold
                </Button>
                <Button size="small" disabled={!r.gold_ready} onClick={() => previewOne(r)}>
                  试跑
                </Button>
              </Space>
            ),
          },
        ]}
      />

      <Drawer
        title="标注 Gold 文档"
        width={640}
        open={detail != null}
        onClose={() => setDetail(null)}
      >
        {detail ? (
          <>
            <Paragraph>{detail.question}</Paragraph>
            <GoldDocEditor
              recordId={detail.id}
              scope={detail.scope}
              goldDocs={detail.eval_gold_docs}
              topK={detail.eval_top_k}
              onSaved={(next) => {
                setDetail({ ...detail, ...next, gold_ready: next.gold_ready })
                void reload(page)
              }}
            />
          </>
        ) : null}
      </Drawer>

      <Modal
        title="单条检索试跑"
        open={previewOpen}
        onCancel={() => setPreviewOpen(false)}
        footer={null}
        width={720}
      >
        {previewLoading ? (
          <Text type="secondary">评测中…</Text>
        ) : previewResult ? (
          <>
            <Space wrap style={{ marginBottom: 12 }}>
              <Tag>Recall@K: {metricCell(previewResult.metrics, 'recall_at_k')}</Tag>
              <Tag>Gold 排名: {metricCell(previewResult.metrics, 'primary_gold_rank')}</Tag>
              <Tag>nDCG: {metricCell(previewResult.metrics, 'ndcg_at_k')}</Tag>
              <Tag>MRR: {metricCell(previewResult.metrics, 'mrr')}</Tag>
            </Space>
            <Table
              size="small"
              rowKey="item_id"
              pagination={false}
              dataSource={previewResult.documents}
              columns={[
                { title: '排名', dataIndex: 'rank', width: 56 },
                { title: '文档', dataIndex: 'title' },
                { title: '分数', dataIndex: 'score', width: 80 },
              ]}
            />
          </>
        ) : null}
      </Modal>

      <Modal
        title="运行检索评测"
        open={runOpen}
        onCancel={() => setRunOpen(false)}
        onOk={startRun}
        okText="开始"
        confirmLoading={running}
      >
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          <div>
            <Text>实验名称</Text>
            <Input
              placeholder="如：专名召回优化 v2"
              value={runName}
              onChange={(e) => setRunName(e.target.value)}
              maxLength={128}
            />
          </div>
          <div>
            <Text>评测范围</Text>
            <Segmented
              block
              value={runTarget}
              onChange={(v) => setRunTarget(v as typeof runTarget)}
              options={[
                { label: '全部星标（已设 Gold）', value: 'benchmark' },
                { label: '全部 Bad（已设 Gold）', value: 'bad' },
                { label: '当前勾选', value: 'selected' },
              ]}
            />
          </div>
          <div>
            <Text>
              与当前基线 AB 对比{' '}
              <Switch
                checked={compareBaseline}
                disabled={!baseline?.batch_id}
                onChange={setCompareBaseline}
              />
            </Text>
            {!baseline?.batch_id ? (
              <Text type="secondary" style={{ fontSize: 12, display: 'block' }}>
                默认与对话中用户标注 Gold 时固化的 Top10 快照对比（nDCG / 排名）；也可在批次详情中「设为基线」。
              </Text>
            ) : null}
          </div>
        </Space>
      </Modal>

      <Drawer
        title="检索评测批次"
        width={720}
        open={batchListOpen}
        onClose={() => setBatchListOpen(false)}
        extra={
          <Button size="small" loading={batchLoading} onClick={() => void reloadBatches()}>
            刷新
          </Button>
        }
      >
        <Table
          rowKey="batch_id"
          size="small"
          loading={batchLoading}
          dataSource={batchList}
          pagination={tablePagination(
            batchPag.page,
            batchTotal,
            batchPag.handlePaginationChange,
            batchPag.paginationOptions,
          )}
          columns={[
            {
              title: '名称',
              render: (_, r) => (
                <Space direction="vertical" size={0}>
                  <Text>{r.experiment_name || r.batch_id}</Text>
                  {r.is_baseline ? <Tag color="gold">基线</Tag> : null}
                </Space>
              ),
            },
            {
              title: '进度',
              width: 100,
              render: (_, r) => `${r.finished_cases}/${r.total_cases}`,
            },
            {
              title: '状态',
              width: 120,
              render: (_, r) => (
                <Space direction="vertical" size={0}>
                  {batchStatusTag(r.status)}
                  {r.status === 'running' || r.status === 'pending' ? (
                    <Progress percent={r.progress} size="small" showInfo={false} />
                  ) : null}
                </Space>
              ),
            },
            {
              title: '汇总',
              render: (_, r) => {
                const s = r.summary
                if (!s || r.status !== 'done') return '—'
                if (s.win != null) {
                  return (
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      胜 {s.win} / 平 {s.tie} / 负 {s.lose}
                    </Text>
                  )
                }
                return (
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    Recall {s.recall_rate ?? '—'} · 均 rank {s.avg_primary_gold_rank ?? '—'}
                  </Text>
                )
              },
            },
            {
              title: '操作',
              width: 120,
              render: (_, r) => (
                <Button size="small" type="link" onClick={() => openBatchDetail(r.batch_id)}>
                  详情
                </Button>
              ),
            },
          ]}
        />
      </Drawer>

      <Drawer
        title={batchDetail?.experiment_name ? `检索 AB：${batchDetail.experiment_name}` : '检索批次详情'}
        width="92%"
        open={batchDetailOpen}
        onClose={() => {
          setBatchDetailOpen(false)
          setBatchDetail(null)
        }}
        loading={batchDetailLoading && !batchDetail}
        extra={
          batchDetail?.status === 'done' && !batchDetail.is_baseline ? (
            <Button size="small" onClick={() => markBaseline(batchDetail.batch_id)}>
              设为基线
            </Button>
          ) : null
        }
      >
        {batchDetail ? (
          <>
            <Space wrap style={{ marginBottom: 12 }}>
              <Tag>{batchDetail.batch_id}</Tag>
              {batchDetail.is_baseline ? <Tag color="gold">当前基线</Tag> : null}
              {batchStatusTag(batchDetail.status)}
              {(batchDetail.status === 'running' || batchDetail.status === 'pending') && (
                <Progress percent={batchDetail.progress} style={{ width: 160 }} size="small" />
              )}
              {batchDetail.summary && batchDetail.status === 'done' ? (
                <>
                  {batchDetail.summary.win != null ? (
                    <>
                      <Tag color="green">提升 {batchDetail.summary.win}</Tag>
                      <Tag>持平 {batchDetail.summary.tie}</Tag>
                      <Tag color="red">回退 {batchDetail.summary.lose}</Tag>
                      <Tag>胜率 {(batchDetail.summary.retrieval_win_rate ?? 0) * 100}%</Tag>
                      {batchDetail.summary.good?.avg_ndcg_baseline != null ? (
                        <Tag>
                          nDCG {batchDetail.summary.good.avg_ndcg_baseline} →{' '}
                          {batchDetail.summary.good.avg_ndcg_candidate}
                        </Tag>
                      ) : null}
                    </>
                  ) : (
                    <>
                      <Tag>Recall {batchDetail.summary.recall_rate ?? '—'}</Tag>
                      <Tag>均 rank {batchDetail.summary.avg_primary_gold_rank ?? '—'}</Tag>
                      <Tag>nDCG {batchDetail.summary.avg_ndcg_at_k ?? '—'}</Tag>
                    </>
                  )}
                </>
              ) : null}
            </Space>
            {batchDetail.error_message ? (
              <Paragraph type="danger">{batchDetail.error_message}</Paragraph>
            ) : null}
            {batchDetail.items.map((r) => (
              <div
                key={r.run_id}
                style={{
                  marginBottom: 16,
                  padding: 14,
                  border: '1px solid #e8ecf0',
                  borderRadius: 10,
                }}
              >
                <Space wrap style={{ marginBottom: 6 }}>
                  <Text strong>#{r.record_id}</Text>
                  {caseTag(r.case_label)}
                  {verdictTag(r.verdict)}
                  {r.compare?.reasons?.length ? (
                    <Tag>{r.compare.reasons.join(', ')}</Tag>
                  ) : null}
                </Space>
                <Paragraph style={{ marginBottom: 8 }}>{r.question}</Paragraph>
                {r.error_message ? (
                  <Text type="danger">{r.error_message}</Text>
                ) : (
                  <>
                    <Space wrap style={{ marginBottom: 8 }}>
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        基线 rank {metricCell(r.baseline_metrics, 'primary_gold_rank')} → 候选{' '}
                        {metricCell(r.metrics, 'primary_gold_rank')}
                      </Text>
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        Recall {metricCell(r.baseline_metrics, 'recall_at_k')} →{' '}
                        {metricCell(r.metrics, 'recall_at_k')}
                      </Text>
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        标注时 nDCG {metricCell(r.baseline_metrics, 'ndcg_at_k')} → 现{' '}
                        {metricCell(r.metrics, 'ndcg_at_k')}
                        {r.compare?.ndcg_delta != null
                          ? ` (Δ ${r.compare.ndcg_delta})`
                          : ''}
                      </Text>
                    </Space>
                    <Table
                      size="small"
                      rowKey="item_id"
                      pagination={false}
                      dataSource={r.documents.slice(0, 10)}
                      columns={[
                        { title: '#', dataIndex: 'rank', width: 40 },
                        {
                          title: '文档',
                          dataIndex: 'title',
                          render: (t: string, row) => (
                            <span>
                              {r.gold_item_ids.includes(row.item_id) ? (
                                <Tag color="gold" style={{ marginRight: 4 }}>
                                  Gold
                                </Tag>
                              ) : null}
                              {t}
                            </span>
                          ),
                        },
                        { title: '分', dataIndex: 'score', width: 72 },
                      ]}
                    />
                  </>
                )}
              </div>
            ))}
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Pagination
                {...listPagination(
                  batchDetailPag.page,
                  batchDetail.total,
                  (p, ps) => {
                    batchDetailPag.handlePaginationChange(p, ps)
                    openBatchDetail(batchDetail.batch_id, p, ps ?? batchDetailPag.pageSize)
                  },
                  batchDetailPag.paginationOptions,
                )}
              />
            </div>
          </>
        ) : batchDetailLoading ? (
          <Text type="secondary">加载中…</Text>
        ) : null}
      </Drawer>
    </div>
  )
}
